/**
 * Screen wording for the weekly 40-hour rule (D-007), and the filler task code's
 * fund rule as the pickers apply it.
 *
 * The server decides everything: whether the week is covered, the minute
 * totals, short/over, the blockers, and the filler code's name
 * (`TimesheetTotals.weeklyTarget`). These helpers only turn that into text, so
 * the tracker, the Submit line, the footer and the add-entry popover never
 * word the same number two ways. Hours always come from whole minutes through
 * `formatHoursFromMinutes`, never from summed decimal hours.
 *
 * The filler code is never written out here: it comes from the server
 * (`weeklyTarget.filler`) or, when comparing with task-code rows, from
 * FILLER_TASK_CODE.
 */

import {
  FILLER_TASK_CODE,
  formatHoursFromMinutes,
  type CachedFund,
  type FillerTaskCodeInfo,
  type WeeklyTarget,
} from '@renewal/types';

/** "6.5 hrs", "1 hr". */
export function hrs(minutes: number): string {
  return `${formatHoursFromMinutes(minutes)} ${minutes === 60 ? 'hr' : 'hrs'}`;
}

/** "6.5 hours", "1 hour". */
export function hoursWords(minutes: number): string {
  return `${formatHoursFromMinutes(minutes)} ${minutes === 60 ? 'hour' : 'hours'}`;
}

/** A covered week has a target; earlier weeks (or no target at all) show a plain total. */
export type CoveredWeeklyTarget = WeeklyTarget & { requiredMinutes: number };

/** The 40-hour rule applies to this week. Absent or `not_applicable` means it does not. */
export function isCoveredWeek(
  target: WeeklyTarget | null | undefined
): target is CoveredWeeklyTarget {
  return !!target && target.status !== 'not_applicable' && target.requiredMinutes !== null;
}

/** "<code> <name>" for the filler as the server names it, or the code alone. */
export function fillerLabel(filler: FillerTaskCodeInfo): string {
  return filler.name ? `${filler.code} ${filler.name}` : filler.code;
}

/**
 * The tracker's one line: "33.5 of 40 hrs, 6.5 to go", "Ready to submit", "2 hrs
 * over, reduce to submit", "33.5 hrs". A week that is not open (submitted or
 * approved) gets the hours only, "40 of 40 hrs", with no call to action.
 */
export function trackerStatusText(target: WeeklyTarget, editable = true): string {
  if (!isCoveredWeek(target)) {
    return hrs(target.totalMinutes);
  }
  const total = formatHoursFromMinutes(target.totalMinutes);
  const required = formatHoursFromMinutes(target.requiredMinutes);
  if (!editable) {
    return `${total} of ${required} hrs`;
  }
  switch (target.status) {
    case 'short':
      return `${total} of ${required} hrs, ${formatHoursFromMinutes(target.shortByMinutes)} to go`;
    case 'over':
      return `${hrs(target.overByMinutes)} over, reduce to submit`;
    default:
      return target.ready ? 'Ready to submit' : `${total} of ${required} hrs`;
  }
}

/** The line beside Submit for a covered week (blockers are listed separately). */
export function submitHoursLine(target: CoveredWeeklyTarget): string {
  const total = formatHoursFromMinutes(target.totalMinutes);
  const required = formatHoursFromMinutes(target.requiredMinutes);
  switch (target.status) {
    case 'short':
      return (
        `${total} of ${required} hours. Add ${hoursWords(target.shortByMinutes)} ` +
        `(use ${fillerLabel(target.filler)} for time you did not work).`
      );
    case 'over':
      return `${hoursWords(target.overByMinutes)} over. Reduce to ${required} to submit.`;
    default:
      return target.ready ? 'Ready to submit.' : `${total} of ${required} hours.`;
  }
}

/** "33.5 hrs worked + 6.5 hrs <filler code> <name> (time not worked) = 40 hrs". */
export function workedFillerLine(target: WeeklyTarget): string {
  return (
    `${hrs(target.workedMinutes)} worked + ${hrs(target.fillerMinutes)} ` +
    `${fillerLabel(target.filler)} (time not worked) = ${hrs(target.totalMinutes)}`
  );
}

export type WeekAfterEntryState = 'short' | 'exact' | 'over' | 'plain';

/**
 * The add-entry popover's week line: where the week lands if this block is
 * saved on every dragged day. Information only; over 40 never blocks a save.
 * Landing on 40 does not say "ready": a dragged day may be after today, or the
 * week may have another blocker, which only the server's week knows.
 */
export function weekAfterEntry(input: {
  afterMinutes: number;
  requiredMinutes: number | null;
  dayCount: number;
}): { state: WeekAfterEntryState; text: string } {
  const { afterMinutes, requiredMinutes } = input;
  const these = input.dayCount > 1 ? 'these entries' : 'this entry';
  const after = formatHoursFromMinutes(afterMinutes);
  if (requiredMinutes === null) {
    return { state: 'plain', text: `This week: ${hrs(afterMinutes)} after ${these}.` };
  }
  const required = formatHoursFromMinutes(requiredMinutes);
  if (afterMinutes < requiredMinutes) {
    return {
      state: 'short',
      text: `This week: ${after} of ${required} hrs after ${these}, ${formatHoursFromMinutes(requiredMinutes - afterMinutes)} to go.`,
    };
  }
  if (afterMinutes === requiredMinutes) {
    return {
      state: 'exact',
      text: `This week: ${after} of ${required} hrs after ${these}.`,
    };
  }
  return {
    state: 'over',
    text:
      `This week: ${hrs(afterMinutes)} after ${these}, ${hrs(afterMinutes - requiredMinutes)} over ${required}. ` +
      `You can save it, but reduce the week to ${required} to submit.`,
  };
}

/** A task-code row is the filler code. */
export function isFillerTaskCode(code: string | null | undefined): boolean {
  return !!code && code.trim().toUpperCase() === FILLER_TASK_CODE;
}

/**
 * The filler may be charged to General Fund (no fund id) or to a fund whose
 * restriction is exactly 'UNRESTRICTED'. A fund not yet synced (null) is
 * refused, as the server refuses it.
 */
export function fundTakesFiller(fund: Pick<CachedFund, 'restrictionType'>): boolean {
  return fund.restrictionType === 'UNRESTRICTED';
}

/** Why a fund is not offered for the filler, appended to its name. */
export function fillerFundSuffix(fund: Pick<CachedFund, 'restrictionType'>): string {
  return fund.restrictionType === null ? ' — not synced yet' : ' — restricted';
}

/** One line under the fund picker while the filler code is chosen. */
export function fillerFundReason(code: string, name: string | null): string {
  const label = name ? `${code} (${name})` : code;
  return `${label} is time not worked and can only be charged to General Fund or another unrestricted fund.`;
}
